import { Box, Grid, useTheme, List, ListItemButton, ListItemText, Typography, Button, Link, Stack } from "@mui/material";
import { DevelopmentItem } from "../models/dtos";
import { useState, useEffect } from "react";
import ConditionalDisplay from "../components/ConditionalDisplay";
import DevelopmentClient, { CompleteDevelopmentItemContext, DevelopmentItemErrors } from "../apiClients/DevelopmentClient";
import IconButtonEx from "../components/IconButtonEx";
import DeleteIcon from "@mui/icons-material/Delete";
import { I18N } from "../I18N";
import { AreNotEqual, IsEmpty, IsNotEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import { useErrorDisplay } from "../components/ErrorDisplay";
import DevelopmentItemCompleteComponent from "./DevelopmentItemCompleteComponent";
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import useViewportMode from "../hooks/useViewportMode";
import { MobileEntityCard } from "../components/responsive";

export interface DevelopmentItemListDisplayComponentProps {
    talentId: string;
    subscriptionActive: boolean;
    data: DevelopmentItem[];
    onChange?: () => void;
}

const DevelopmentItemListDisplayComponent = ({ talentId, subscriptionActive, data, onChange = () => { } }: DevelopmentItemListDisplayComponentProps) => {

    const defaultItem = DevelopmentClient.CreateDefault();

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();
    const singlePane = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    const [hasDataChanged, setHasDataChanged] = useState<boolean>(false);
    const [listItemSelected, setListItemSelected] = useState<DevelopmentItem>(defaultItem);

    const [activeList, setActiveList] = useState<DevelopmentItem[]>([]);
    const [completeList, setCompleteList] = useState<DevelopmentItem[]>([]);
    const [showSelectedDetail, setShowSelectedDetail] = useState(false);

    useEffect(() => {
        // Set first item as selected by default
        if (data.length > 0) {
            const active = data.filter(f => IsEmpty(f.CompleteDate));
            const complete = data.filter(f => IsNotEmpty(f.CompleteDate));

            setActiveList(active);
            setCompleteList(complete);

            if (active.length > 0) {
                setListItemSelected(active[0]);
            }
            else {
                setListItemSelected(complete[0]);
            }
            setShowSelectedDetail(false);
        }
        else {
            setActiveList([]);
            setCompleteList([]);
            setListItemSelected(DevelopmentClient.CreateDefault());
            setShowSelectedDetail(false);
        }
    }, [data]);

    const handleListItemClick = (e, i: string) => {
        let n = data.find(f => f.Id === i);
        setListItemSelected(n);
        if (singlePane)
            setShowSelectedDetail(true);
    };

    const handleDelete = async (id: string) => {
        try {
            setBusy(true);

            await new DevelopmentClient().Delete(id);
            onChange();
        }
        catch (error) {
            setError(error, DevelopmentItemErrors.Delete);
        }
        finally {
            setBusy(false);
        }
    }

    const handleOnChange = () => {
        setHasDataChanged(true);
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);

            const context: CompleteDevelopmentItemContext = { CompletedDate: listItemSelected.CompleteDate };
            await new DevelopmentClient().Complete(listItemSelected.Id, context);

            setHasDataChanged(false);
            onChange();

        } catch (error) {
            setError(error, DevelopmentItemErrors.Update);
        } finally {
            setBusy(false);
        }
    }

    const getAlternateDisplayGridContainerStyle = () => {
        return { width: '100%', marginTop: theme.spacing(0) };
    }

    if (!subscriptionActive)
        if (!viewport.isDesktop)
            return <Box sx={{ p: 2 }}><Typography variant="body2">You need an active subscription for Development Plans, <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">subscribe here</Link></Typography></Box>;
        else
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            {`You need an active subscription for Development Plans, `}
                            <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">{`subscribe here`}</Link>
                        </Typography>
                        
                    </Grid>
                </Grid>
            </>
        )
    else if (singlePane)
        return (
            <Box>
                <ErrorDisplay />
                <BusySpinner busy={busy}>
                    {showSelectedDetail && AreNotEqual(listItemSelected, defaultItem) ? (
                        <Stack spacing={2} sx={{ p: 2 }} data-testid="development-item-detail">
                            <Button
                                startIcon={<ArrowBackRoundedIcon />}
                                onClick={() => setShowSelectedDetail(false)}
                                sx={{ minHeight: 44, alignSelf: 'flex-start' }}
                            >
                                Development items
                            </Button>
                            <DevelopmentItemCompleteComponent developmentItem={listItemSelected} onChange={handleOnChange} />
                            <Button disabled={!hasDataChanged} variant="outlined" onClick={handleSubmit} sx={{ minHeight: 44 }}>Submit</Button>
                        </Stack>
                    ) : (
                        <Stack spacing={2} sx={{ p: 1.5 }} data-testid="development-item-cards">
                            {data.length <= 0 && <Typography variant="body2">What's the one thing that would really move the needle?</Typography>}
                            {activeList.length > 0 && <Typography variant="subtitle2">Active Items</Typography>}
                            {activeList.map(item => (
                                <MobileEntityCard
                                    key={item.Id}
                                    title={item.Title}
                                    status="Active"
                                    actions={<>
                                        <Button onClick={(event) => handleListItemClick(event, item.Id)}>View</Button>
                                        <IconButtonEx
                                            hoverContent={I18N.Translate('Delete Development Item')}
                                            message={`${I18N.Translate('Are you sure you want to delete')} '${item.Title}'`}
                                            onClick={() => handleDelete(item.Id)}
                                        >
                                            <DeleteIcon htmlColor="red" />
                                        </IconButtonEx>
                                    </>}
                                    ariaLabel={`Active development item ${item.Title}`}
                                />
                            ))}
                            {completeList.length > 0 && <Typography variant="subtitle2">Complete Items</Typography>}
                            {completeList.map(item => (
                                <MobileEntityCard
                                    key={item.Id}
                                    title={item.Title}
                                    status="Complete"
                                    actions={<Button onClick={(event) => handleListItemClick(event, item.Id)}>View</Button>}
                                    ariaLabel={`Completed development item ${item.Title}`}
                                />
                            ))}
                        </Stack>
                    )}
                </BusySpinner>
            </Box>
        );
    else
    return (
        <Box>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                <Grid container spacing={1} sx={{ width: '100%', marginTop: theme.spacing(0) }}>
                    {/* Development Item Title List */}
                    <Grid item xs={3}>
                        <ConditionalDisplay ifTrue={activeList.length > 0}>
                            <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', marginLeft: '.5em', marginRight: '.5em', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                                <Box sx={{ marginRight: '.5em', padding: '.25rem', border: 1, borderColor: theme.palette.primary.contrastText }}>
                                    <Typography>Active Items</Typography>
                                </Box>
                                <List sx={{ marginRight: '.5em' }}>
                                    {activeList.map(di =>
                                        <ListItemButton key={di.Id}
                                            selected={listItemSelected?.Id === di.Id}
                                            onClick={(event) => handleListItemClick(event, di.Id)}>
                                            <ListItemText primary={di.Title} />
                                            <ConditionalDisplay ifTrue={listItemSelected.Id === di.Id}>
                                                {/* <OnlyForRole role={}> */}
                                                <IconButtonEx
                                                    size='small'
                                                    hoverContent={I18N.Translate('Delete Development Item')}
                                                    message={`${I18N.Translate('Are you sure you want to delete')} '${di.Title}'`}
                                                    onClick={() => handleDelete(di.Id)}><DeleteIcon htmlColor='red' fontSize='small' />
                                                </IconButtonEx>
                                                {/* </OnlyForRole>) */}
                                            </ConditionalDisplay>
                                        </ListItemButton>
                                    )}
                                </List>
                                <Box sx={{ marginRight: '.5em', padding: '.25rem', border: 1, borderColor: theme.palette.primary.contrastText }}>
                                    <Typography>Complete Items</Typography>
                                </Box>
                                <List sx={{ marginRight: '.5em' }}>
                                    {completeList.map(di =>
                                        <ListItemButton key={di.Id}
                                            selected={listItemSelected?.Id === di.Id}
                                            onClick={(event) => handleListItemClick(event, di.Id)}>
                                            <ListItemText primary={di.Title} />
                                        </ListItemButton>
                                    )}
                                </List>
                            </Box>
                        </ConditionalDisplay>
                    </Grid>
                    {/* Development Content */}
                    <Grid item xs={9}>
                        <ConditionalDisplay ifTrue={data.length <= 0}>
                            <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>What's the one thing that would really move the needle?</Typography>
                        </ConditionalDisplay>
                        <ConditionalDisplay ifTrue={AreNotEqual(listItemSelected, defaultItem)}>
                            <>
                                <DevelopmentItemCompleteComponent developmentItem={listItemSelected} onChange={handleOnChange} />
                                <Box sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2), display: 'flex', flexDirection: 'row-reverse' }}>
                                    <Button disabled={!hasDataChanged} variant='outlined' onClick={handleSubmit}>Submit</Button>
                                </Box>
                            </>
                        </ConditionalDisplay>
                    </Grid>
                </Grid>
            </BusySpinner>
        </Box >
    );
}

export default DevelopmentItemListDisplayComponent;
